import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Star, Calendar, ArrowRight } from "lucide-react";
import { LxpLayout } from "@/components/lexpractiq/LxpLayout";
import { listPublishedMentors, requestMentorSession } from "@/lib/api/lxp-mentors.functions";

export const Route = createFileRoute("/_authenticated/lexpractiq/mentors/")({
  head: () => ({ meta: [{ title: "Mentor Network | SA LexPractiq" }] }),
  component: MentorsPage,
});

function MentorsPage() {
  const listFn = useServerFn(listPublishedMentors);
  const reqFn = useServerFn(requestMentorSession);
  const [mentors, setMentors] = useState<any[]>([]);
  const [active, setActive] = useState<any | null>(null);
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => { listFn().then((r) => setMentors(r.mentors)); }, []);

  async function submit() {
    if (!active || topic.length < 4) return toast.error("Add a session topic");
    try {
      await reqFn({ data: { mentorId: active.id, topic, studentNotes: notes } });
      toast.success("Request sent to mentor");
      setActive(null); setTopic(""); setNotes("");
    } catch (e: any) { toast.error(e?.message || "Failed to request"); }
  }

  return (
    <LxpLayout>
      <div className="lxp-container" style={{ padding: "2rem 1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" }}>
          <div>
            <span className="lxp-badge"><Users size={12} /> Mentor Network</span>
            <h1 style={{ margin: "0.5rem 0 0.25rem" }}>Practising lawyers ready to guide you</h1>
            <p className="lxp-muted" style={{ margin: 0 }}>Book Q&amp;A, request structured feedback, and get career guidance.</p>
          </div>
          <Link to="/lexpractiq/mentors/apply" className="lxp-btn-ghost">Become a mentor →</Link>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "0.85rem" }}>
          {mentors.map((m) => (
            <div key={m.id} className="lxp-card">
              <strong>{m.display_name}</strong>
              <div className="lxp-muted" style={{ fontSize: "0.85rem" }}>{m.headline}</div>
              <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", margin: "0.5rem 0" }}>
                {(m.specialisations || []).slice(0, 4).map((s: string) => <span key={s} className="lxp-badge">{s}</span>)}
              </div>
              <p className="lxp-muted" style={{ fontSize: "0.85rem", lineHeight: 1.5 }}>{(m.bio || "").slice(0, 160)}</p>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0.5rem", fontSize: "0.8rem", color: "var(--lxp-text-muted)" }}>
                <span><Star size={12} style={{ verticalAlign: "-2px" }} /> {m.years_experience ?? "—"} yrs</span>
                <button className="lxp-btn" onClick={() => setActive(m)} style={{ fontSize: "0.8rem" }}>
                  <Calendar size={12} /> Request session
                </button>
              </div>
            </div>
          ))}
          {mentors.length === 0 && <p className="lxp-muted">No mentors published yet. Check back soon.</p>}
        </div>

        {active && (
          <div className="lxp-card" style={{ marginTop: "1.25rem", borderColor: "var(--lxp-accent)" }}>
            <h3 style={{ margin: "0 0 0.5rem" }}>Request a session with {active.display_name}</h3>
            <label style={{ display: "block", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
              <div className="lxp-muted">Topic</div>
              <input className="lxp-input" value={topic} onChange={(e) => setTopic(e.target.value)} />
            </label>
            <label style={{ display: "block", fontSize: "0.85rem" }}>
              <div className="lxp-muted">Notes for mentor (optional)</div>
              <textarea className="lxp-textarea" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
              <button className="lxp-btn" onClick={submit}>Send request <ArrowRight size={14} /></button>
              <button className="lxp-btn-ghost" onClick={() => setActive(null)}>Cancel</button>
            </div>
          </div>
        )}

        <div style={{ marginTop: "1.5rem" }}>
          <Link to="/lexpractiq/mentors/sessions" className="lxp-btn-ghost">My sessions →</Link>
        </div>
      </div>
    </LxpLayout>
  );
}
